import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
  IonBackButton,
} from '@ionic/react';
import { settingsOutline } from 'ionicons/icons';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { auth } from '../../../firebase';
import { calendarRepository, workoutRepository } from '../../../repositories/LocalRepositories';
import { CalendarLog } from '../../../datamodels';

import CycleDayReview from './CycleDayReview';
import CalendarLegend from './CalendarLegend';
import LogModal from './LogModal';
import SettingsModal from './SettingsModal';
import './CalendarWidget.css';

// Format a Date to YYYY-MM-DD (for storage)
const formatLocal = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Parse a “YYYY-MM-DD” string into a local‐time Date at midnight
const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Four‐phase logic
const getPhaseForDay = (
  day: number,
  cycleLength: number,
  periodLength: number
): 'menstruation' | 'follicular' | 'ovulation' | 'luteal' | null => {
  const ovulationDay = cycleLength - 14;
  if (day >= 1 && day <= periodLength) return 'menstruation';
  if (day > periodLength && day < ovulationDay) return 'follicular';
  if (day === ovulationDay) return 'ovulation';
  if (day > ovulationDay && day <= cycleLength) return 'luteal';
  return null;
};

const CalendarWidget: React.FC = () => {
  // Data
  const [logs, setLogs] = useState<CalendarLog[]>([]);
  const [workoutDates, setWorkoutDates] = useState<Set<string>>(new Set());

  // Log modal
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [mood, setMood] = useState<'good' | 'neutral' | 'bad'>();
  const [note, setNote] = useState('');
  const [onPeriod, setOnPeriod] = useState(false);
  const [workoutTitle, setWorkoutTitle] = useState('');
  const [workoutTime, setWorkoutTime] = useState('18:00');
  const [showLogModal, setShowLogModal] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Month navigation
  const [activeMonth, setActiveMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const swipeStartX = useRef<number | null>(null);
  const didSwipe = useRef(false);

  // Settings modal
  const savedCalendarSettings = (() => {
    try { return JSON.parse(localStorage.getItem(`pl_calendar_settings_${auth.currentUser?.uid || 'local'}`) || '{}'); }
    catch { return {}; }
  })();
  const [cycleLength, setCycleLength] = useState(Number(savedCalendarSettings.cycleLength) || 28);
  const [periodLength, setPeriodLength] = useState(Number(savedCalendarSettings.periodLength) || 4);
  const [showSettings, setShowSettings] = useState(false);

  const userId = auth.currentUser?.uid;
  const msPerDay = 1000 * 60 * 60 * 24;

  useEffect(() => {
    localStorage.setItem(`pl_calendar_settings_${userId || 'local'}`, JSON.stringify({ cycleLength, periodLength }));
  }, [cycleLength, periodLength, userId]);

  // Fetch logs + workouts
  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLogs(await calendarRepository.list(userId));
      const sessions = await workoutRepository.list(userId);
      const dates = new Set<string>();
      sessions.forEach(data => {
        if (data.start_date) {
          const { seconds, nanoseconds = 0 } = data.start_date as any;
          const dt = new Date(seconds * 1000 + nanoseconds / 1e6);
          dates.add(formatLocal(dt));
        }
      });
      setWorkoutDates(dates);
    })();
  }, [userId]);

  // Compute exact period days for each tick
  const periodDays = useMemo<Set<string>>(() => {
    const days = new Set<string>();
    logs
      .filter(l => l.on_period)
      .forEach(l => {
        const start = parseLocalDate(l.date).getTime();
        for (let i = 0; i < periodLength; i++) {
          days.add(formatLocal(new Date(start + i * msPerDay)));
        }
      });
    return days;
  }, [logs, periodLength]);

  // Helper: most recent period start on or before date
  const getRecentPeriodStartBefore = (date: Date): Date | null => {
    const valid = logs
      .filter(l => l.on_period)
      .map(l => parseLocalDate(l.date))
      .filter(d => d.getTime() <= date.getTime());
    if (!valid.length) return null;
    return new Date(Math.max(...valid.map(d => d.getTime())));
  };

  // CycleDayReview values
  const today = new Date();
  const lastStart = getRecentPeriodStartBefore(today) ?? today;
  const currentDay =
    Math.floor((today.getTime() - lastStart.getTime()) / msPerDay) + 1;
  const nextPeriodInDays = cycleLength - currentDay + 1;
  const lpStart = lastStart;
  const lpEnd = new Date(lpStart.getTime() + (periodLength - 1) * msPerDay);
  const lastPeriod = `${lpStart.toLocaleString('default', {
    month: 'short',
  })} ${lpStart.getDate()} – ${lpEnd.getDate()}`;
  const currentPhase =
    getPhaseForDay(currentDay, cycleLength, periodLength) ?? 'follicular';

  // Open log modal
  const openDay = (date: Date) => {
    setSaveError('');
    setSelectedDate(date);
    const ds = formatLocal(date);
    const existing = logs.find(l => l.date === ds);
    if (existing) {
      setMood(existing.mood);
      setNote(existing.note || '');
      setOnPeriod(existing.on_period || false);
      setWorkoutTitle(existing.workout_title || '');
      setWorkoutTime(existing.workout_time || '18:00');
    } else {
      setMood(undefined);
      setNote('');
      setOnPeriod(false);
      setWorkoutTitle('');
      setWorkoutTime('18:00');
    }
    setShowLogModal(true);
  };

  // Save log
  const saveLog = async () => {
    if (!userId || !selectedDate) return;
    const ds = formatLocal(selectedDate);
    const hasWorkoutPlan = Boolean(workoutTitle.trim());
    const newLog: CalendarLog = {
      user_id: userId,
      date: ds,
      mood,
      note: note.trim(),
      on_period: onPeriod,
      planned_workout: hasWorkoutPlan,
      workout_title: hasWorkoutPlan ? workoutTitle.trim() : '',
      workout_time: hasWorkoutPlan ? workoutTime || '18:00' : '',
    };
    try {
      setSaveError('');
      const saved = await calendarRepository.save(newLog);
      setLogs(current => [...current.filter(log => log.date !== ds), saved]);
      setShowLogModal(false);
    } catch (error) {
      console.error('Could not save calendar day:', error);
      setSaveError('Could not save this day locally. Please try again.');
    }
  };

  // Tile CSS classes
  const getTileClass = (date: Date) => {
    const classes: string[] = [];
    const ds = formatLocal(date);

    if (periodDays.has(ds)) {
      classes.push('calendar-tile--menstruation');
    } else {
      const start = getRecentPeriodStartBefore(date);
      if (start) {
        const diff =
          Math.floor((date.getTime() - start.getTime()) / msPerDay) + 1;
        const phase = getPhaseForDay(diff, cycleLength, periodLength);
        if (phase === 'ovulation') {
          classes.push('calendar-tile--ovulation');
        }
      }
    }

    if (ds === formatLocal(new Date())) {
      classes.push('calendar-tile--today');
    }
    return classes;
  };

  // Tile content dots
  const renderTileContent = ({ date }: { date: Date }) => {
    const ds = formatLocal(date);
    const hasWorkout = workoutDates.has(ds);
    const hasLog = logs.some(l => l.date === ds);
    const hasPlannedWorkout = logs.some(l => l.date === ds && l.planned_workout);
    return (
      <div className="tile-dots">
        {hasWorkout && <span className="dot--workout" />}
        {hasPlannedWorkout && <span className="dot--planned" />}
        {hasLog && <span className="dot--note" />}
      </div>
    );
  };

  const existingLog = selectedDate
    ? logs.find(l => l.date === formatLocal(selectedDate))
    : undefined;

  const calendarRange = () => {
    if (!selectedDate) return null;
    const [hours, minutes] = (workoutTime || '18:00').split(':').map(Number);
    const start = new Date(selectedDate);
    start.setHours(hours || 0, minutes || 0, 0, 0);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const compact = (date: Date) => `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}00`;
    return { start, end, compactStart: compact(start), compactEnd: compact(end) };
  };

  const downloadCalendarEvent = () => {
    const range = calendarRange(); if (!range) return;
    const escape = (value: string) => value.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
    const ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//PawerLifting//Local Calendar//EN', 'BEGIN:VEVENT', `UID:${Date.now()}@pawerlifting.local`, `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}`, `DTSTART:${range.compactStart}`, `DTEND:${range.compactEnd}`, `SUMMARY:${escape(workoutTitle.trim() || 'Workout')}`, 'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
    const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
    const link = document.createElement('a'); link.href = url; link.download = `pawerlifting-${formatLocal(range.start)}.ics`; link.click(); URL.revokeObjectURL(url);
  };

  const openGoogleCalendar = () => {
    const range = calendarRange(); if (!range) return;
    const params = new URLSearchParams({ action: 'TEMPLATE', text: workoutTitle.trim() || 'Workout', dates: `${range.compactStart}/${range.compactEnd}`, details: 'Planned with PawerLifting' });
    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const moveMonth = (offset: number) => {
    setActiveMonth(current => new Date(current.getFullYear(), current.getMonth() + offset, 1));
  };

  const handleCalendarTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    swipeStartX.current = event.touches[0]?.clientX ?? null;
    didSwipe.current = false;
  };

  const handleCalendarTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (swipeStartX.current === null) return;
    const endX = event.changedTouches[0]?.clientX ?? swipeStartX.current;
    const distance = endX - swipeStartX.current;
    swipeStartX.current = null;

    if (Math.abs(distance) < 45) return;
    didSwipe.current = true;
    moveMonth(distance < 0 ? 1 : -1);
    window.setTimeout(() => { didSwipe.current = false; }, 300);
  };

  const handleDayClick = (date: Date) => {
    if (didSwipe.current) return;
    openDay(date);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/my/advisor" /></IonButtons>
          <IonTitle>Calendar</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setShowSettings(true)}>
              <IonIcon icon={settingsOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="calendar-content">
        <main className="calendar-shell">
        <section className="calendar-intro">
          <div><span>Your private rhythm</span><h1>Plan gently, train consistently</h1><p>Workouts, mood and cycle notes stay together on this device.</p><IonButton shape="round" onClick={() => openDay(new Date())}>Log today</IonButton></div>
          <img src="/assets/ploppy/ploppy-calendar-assistant.png" alt="Ploppy holding a calendar" />
        </section>
        <CycleDayReview
          currentDay={currentDay}
          cycleLength={cycleLength}
          periodLength={periodLength}
          lastPeriod={lastPeriod}
          nextPeriodInDays={nextPeriodInDays}
          currentPhase={currentPhase}
          onLogToday={() => openDay(new Date())}
          hasCycleData={logs.some(log => log.on_period)}
        />
        <section className="calendar-month-card"><div className="calendar-section-heading"><span>Your calendar</span><h2>Your month at a glance</h2></div><div className="calendar-swipe-area" onTouchStart={handleCalendarTouchStart} onTouchEnd={handleCalendarTouchEnd}><Calendar activeStartDate={activeMonth} onActiveStartDateChange={({ activeStartDate }) => activeStartDate && setActiveMonth(activeStartDate)} onClickDay={handleDayClick} tileClassName={({ date }) => getTileClass(date)} tileContent={renderTileContent} /></div><CalendarLegend /></section>
        </main>
      </IonContent>

      <LogModal
        isOpen={showLogModal}
        onDismiss={() => setShowLogModal(false)}
        dateStr={selectedDate ? formatLocal(selectedDate) : ''}
        mood={mood}
        setMood={setMood}
        note={note}
        setNote={setNote}
        onPeriod={onPeriod}
        setOnPeriod={setOnPeriod}
        workoutTitle={workoutTitle}
        setWorkoutTitle={setWorkoutTitle}
        workoutTime={workoutTime}
        setWorkoutTime={setWorkoutTime}
        saveLog={saveLog}
        downloadCalendarEvent={downloadCalendarEvent}
        openGoogleCalendar={openGoogleCalendar}
        existingLog={existingLog}
        saveError={saveError}
      />

      <SettingsModal
        isOpen={showSettings}
        onDismiss={() => setShowSettings(false)}
        cycleLength={cycleLength}
        periodLength={periodLength}
        setCycleLength={setCycleLength}
        setPeriodLength={setPeriodLength}
      />
    </IonPage>
  );
};

export default CalendarWidget;
